import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Footer from './components/common/Footer/Footer';
import Login from './components/auth/Login/Login';
import Signup from './components/auth/Signup/Signup';
import Verification from './components/auth/Verification/Verification';
import PasswordSetup from './components/auth/PasswordSetup/PasswordSetup';
import ChangePassword from './components/auth/ChangePassword/ChangePassword';
import HomeOwnerDashboard from './pages/HomeOwner/HomeOwnerDashboard';
import Chat from './pages/Chat/Chat';
import ForgotPassword from './components/auth/ForgotPassword/ForgotPassword';
import SendMessage from './components/support/contact_us/send_message/send_message';
import Profile from './pages/Profile/Profile';
import Settings from './pages/Settings/Settings';
import FloatingChat from './components/common/FloatingChat/FloatingChat';
import ComparisonRouter from './pages/HomeOwner/ComparisonRouter';
import HomeOwnerPricing from './pages/HomeOwner/HomeOwnerPricing';
import HomeOwnerSupport from './pages/HomeOwner/HomeOwnerSupport';
import HomeOwnerBilling from './pages/HomeOwner/HomeOwnerBilling';
import HomeOwnerPlanCheckout from './pages/HomeOwner/HomeOwnerPlanCheckout';
import CreateComparison from './pages/HomeOwner/CreateComparison';


// Resource components removed due to missing files

// Admin imports removed due to missing files

// Import HomeOwner components
import HomeOwnerNavbar from './components/homeowner/HomeOwnerNavbar/HomeOwnerNavbar';
import HomeOwner from './pages/HomeOwner/HomeOwner';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return !!(token && user);
  });

  const [isAdmin, setIsAdmin] = useState(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user?.role === 'admin';
  });

  useEffect(() => {
    const handleStorageChange = () => {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      setIsAuthenticated(!!(token && user));
      setIsAdmin(user?.role === 'admin');
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Protected Route component with role check
  const ProtectedRoute = ({ children, requireAdmin }) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" />;
    }
    if (requireAdmin && !isAdmin) {
      return <Navigate to="/dashboard" />;
    }
    return children;
  };

  // Check if the current path is a homeowner path
  const isHomeOwnerPath = (pathname) => {
    return pathname.startsWith('/homeowner') || pathname === '/';
  };

  // Get the appropriate navbar based on the path and user role
  const getNavbar = () => {
    // Get user if authenticated
    const user = isAuthenticated ? JSON.parse(localStorage.getItem('user') || '{}') : null;
    
    if (isAuthenticated) {
      // Show navbar based on user role, regardless of current path
      if (user?.role === 'homeowner') {
        return <HomeOwnerNavbar />;
      } else {
        // For all other roles (like 'user'), show homeowner navbar for now
        return <HomeOwnerNavbar />;
      }
    } else if (isHomeOwnerPath(window.location.pathname)) {
      // Not logged in but on homeowner path
      return <HomeOwnerNavbar />;
    } else {
      return <HomeOwnerNavbar />;
    }
  };

  return (
    <Router>
      <div className="App">
        {getNavbar()}
        <Routes>
          <Route path="/" element={<HomeOwner />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/verify" element={<Verification />} />
          <Route path="/password-setup" element={<PasswordSetup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route
            path="/homeowner/dashboard"
            element={
              <ProtectedRoute>
                <HomeOwnerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/homeowner/comparisons/:id"
            element={<ComparisonRouter />}
          />
          <Route
            path="/chat"
            element={
              <ProtectedRoute>
                <Chat />
              </ProtectedRoute>
            }
          />
          <Route path="/support/contact" element={<SendMessage />} />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/change-password" 
            element={
              <ProtectedRoute>
                <ChangePassword />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          {/* Resources Routes removed due to missing files */}

          {/* HomeOwner routes */}
          <Route path="/homeowner" element={<HomeOwner />} />
          <Route path="/homeowner/solutions" element={<div>Home Owner Solutions</div>} />
          <Route path="/homeowner/pricing" element={<HomeOwnerPricing />} />
          <Route path="/homeowner/upgrade" element={<HomeOwnerPricing />} />
          <Route path="/homeowner/support" element={<HomeOwnerSupport />} />
          <Route path="/homeowner/contact" element={<HomeOwnerSupport />} />
          <Route path="/homeowner/knowledge-base" element={<div>Knowledge Base Coming Soon</div>} />
          <Route path="/homeowner/tutorials" element={<div>Video Tutorials Coming Soon</div>} />
          <Route path="/homeowner/find-contractors" element={<div>Find Contractors</div>} />
          <Route path="/homeowner/how-it-works" element={<div>How It Works</div>} />
          <Route path="/homeowner/projects/:type" element={<div>Project Details</div>} />
          <Route path="/homeowner/login" element={<Login />} />
          <Route path="/homeowner/signup" element={<Signup />} />
          <Route path="/homeowner/forgot-password" element={<ForgotPassword />} />
          <Route path="/homeowner/profile" element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          } />
          <Route path="/homeowner/settings" element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          } />
          <Route path="/homeowner/billing" element={
            <ProtectedRoute>
              <HomeOwnerBilling />
            </ProtectedRoute>
          } />
          <Route path="/homeowner/checkout" element={
            <ProtectedRoute>
              <HomeOwnerPlanCheckout />
            </ProtectedRoute>
          } />
          <Route path="/homeowner/create-comparison" element={<CreateComparison />} />
        </Routes>
        <Footer />
        {isAuthenticated && !isAdmin && !isHomeOwnerPath(window.location.pathname) && <FloatingChat />}
      </div>
    </Router>
  );
}

export default App;
